import inViewport from '../utils/inViewport'

const HLS_LOADED_CLASS = 'is-hls-loaded'
let HlsModule = null

/**
 * Dynamically loads the hls.js library if it hasn't been loaded already.
 * @returns {Promise<Hls>} The Hls class.
 */
async function getHlsModule() {
  if (!HlsModule) {
    try {
      // Dynamic import of hls.js
      const module = await import('hls.js')
      HlsModule = module.default // hls.js exports the Hls class as default
    } catch (error) {
      console.warn('Failed to load hls.js:', error)
      throw error // Re-throw the error so the component can handle it
    }
  }
  return HlsModule
}

/**
 * Initializes an HLS video element.
 * @param {HTMLVideoElement} video - The video element to initialize.
 * @param {Hls} Hls - The Hls class (already loaded).
 * @param {Object} config - Configuration for the video.
 */
function initVideo(video, Hls, config) {
  if (video.classList.contains(HLS_LOADED_CLASS)) {
    console.warn('Video already initialized with HLS:', video)
    return
  }

  const autoplay = config.autoplay !== undefined ? config.autoplay : true
  const loop = config.loop !== undefined ? config.loop : true
  const playsWhenEnterViewport = config.playsWhenEnterViewport !== undefined ? config.playsWhenEnterViewport : true

  const videoSrc = video.dataset.url ? video.dataset.url : ''

  if (Hls.isSupported()) {
    const hls = new Hls()
    hls.loadSource(videoSrc)
    hls.attachMedia(video)
  } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
    // For browsers that natively support HLS (e.g., Safari)
    video.setAttribute('src', videoSrc)
  } else {
    console.warn('HLS is not supported and native playback failed for video:', video)
    return
  }

  video.classList.add(HLS_LOADED_CLASS)

  video.muted = true
  video.autoplay = autoplay
  video.loop = loop
  video.playsInline = autoplay // playsInline is important for autoplay on mobile

  if (playsWhenEnterViewport) {
    // Ensure inViewport is correctly implemented and functional
    inViewport(video, element => {
      if (element.isIntersecting) {
        // Stop observing once the video is in the viewport and playback is attempted
        // To avoid multiple play() calls or unnecessary re-observations
        // if (element.observer) element.observer.unobserve(element.target);

        ;['canplaythrough', 'ended'].forEach(event => {
          video.addEventListener(event, () => {
            const timeout = event === 'ended' ? 10 : 0
            setTimeout(() => {
              video
                .play()
                .then(() => {
                  // console.log(`Video played on event ${event}`)
                })
                .catch(error => {
                  // console.log(`Error calling play() on video on event${event}`, error)
                })
            }, timeout)
          })
        })
      }
    })
  }
}

/**
 * Loads and/or initializes HLS videos within a given element.
 * @param {HTMLElement} elem - The container element to search for HLS videos.
 * @param {Object} config - Configuration for the videos.
 */
export async function loadHlsVideoByElem(elem, config = {}) {
  // Search only for videos that have not been loaded with HLS.js
  const videoElements = elem.querySelectorAll('.hls-video:not(.is-hls-loaded)')

  if (videoElements.length) {
    try {
      const Hls = await getHlsModule() // Dynamically load Hls.js
      videoElements.forEach(video => initVideo(video, Hls, config))
    } catch (error) {
      console.error('Could not initialize HLS videos due to library loading failure.', error)
    }
  }
}

/**
 * Loads and/or initializes HLS videos based on a CSS selector.
 * This function is useful for global initialization or when targeting
 * videos across different parts of the DOM.
 * @param {string} baseSelector - The CSS selector to find HLS video elements.
 * @param {Object} config - Configuration for the videos.
 */
export async function loadHlsVideo(baseSelector, config = {}) {
  // Select all HLS video elements within the baseSelector that haven't been initialized yet
  const videoElements = document.querySelectorAll(`${baseSelector} .hls-video:not(.is-hls-loaded)`)

  if (videoElements.length) {
    try {
      // Dynamically get the Hls class. This will load hls.js only once.
      const Hls = await getHlsModule()
      // Initialize each found video element
      videoElements.forEach(video => initVideo(video, Hls, config))
    } catch (error) {
      console.warn(
        `Could not initialize HLS videos for selector ${baseSelector} due to library loading failure.`,
        error
      )
    }
  }
}
